import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Conversation } from '@twilio/conversations';
import {
  Activity,
  Button,
  ForEach,
  Icon,
  MessageDisplay,
  P,
} from '@fashionnovateam/nova-ui-toolkit';
import {
  ChatMessage,
  useChatContext,
  useChatMessages,
  useConversationById,
  useLastMessage,
  useParticipantFromMessage,
  useUserFromParticipant,
} from '../../../Chat';
import { ChatUserTooltipContent } from '../../../Chat/ChatUserTooltip';
import {
  useGetReorderChatMessages,
  useGetReorderActivities,
} from '../../../../Lib/Hooks/reorder-chat.hooks';
import {
  BackendChatMessage,
  ReorderWorksheetActivity,
  ReorderWorksheetActivityEnum,
} from '../../../../Lib/Api/types/reorder-chat';
import { type FeedItem } from './types';
import { OpenChatActivity } from './activities/OpenChatActivity';
import { CloseChatActivity } from './activities/CloseChatActivity';
import { UpdateFormActivity } from './activities/UpdateFormActivity';
import { MessageInput } from './MessageInput';
import { getReorderChatId } from './utils';

// -- Reusable sub-components for rendering feed items --

function ConversationViewMessage(props: {
  message: ChatMessage;
  isLast: boolean;
}) {
  const { message, isLast } = props;
  const { userId, identity } = useChatContext();

  const participantProcess = useParticipantFromMessage(
    userId,
    message.instance,
  );
  const userProcess = useUserFromParticipant(
    userId,
    participantProcess.data?.instance,
  );

  const isVendor =
    userProcess?.data?.attributes.type === 'vp' &&
    ['VENDOR', 'VENDOR_STAFF'].includes(userProcess.data.attributes.role);
  const isInfluencer =
    userProcess?.data?.attributes.type === 'vp' &&
    ['INFLUENCER'].includes(userProcess.data.attributes.role);
  const isInternal =
    userProcess?.data?.attributes.type === 'internal' ||
    (userProcess?.data?.attributes.type === 'vp' &&
      !['VENDOR', 'VENDOR_STAFF', 'INFLUENCER'].includes(
        userProcess.data.attributes.role,
      ));

  const badge =
    (isVendor && 'vendor') ||
    (isInfluencer && 'influencer') ||
    (isInternal && 'fn_internal') ||
    undefined;

  return (
    <Activity
      name={userProcess.data?.friendlyName || ''}
      badgeType={badge}
      hideBar={isLast}
      tooltipContent={
        <ChatUserTooltipContent
          name=""
          chatUser={userProcess.data}
          badgeType={badge}
        />
      }
      tooltipZIndex={50}
    >
      <Activity.Message
        name={userProcess.data?.friendlyName || ''}
        date={message.instance.dateCreated || new Date()}
        isPersonal={identity === participantProcess.data?.identity}
        tooltipZIndex={50}
      >
        {message.attributes.type === 'tiptap' ? (
          <MessageDisplay
            content={message.body}
            files={message.files}
            mentionUser={{ userId: identity }}
          />
        ) : (
          <P>{message.body}</P>
        )}
      </Activity.Message>
    </Activity>
  );
}

function ChatBackMessageItem(props: {
  message: BackendChatMessage;
  isLast: boolean;
}) {
  const { message, isLast } = props;
  const { identity } = useChatContext();

  const isVendor =
    message.author?.attributes.type === 'vp' &&
    ['VENDOR', 'VENDOR_STAFF'].includes(message.author.attributes.role);
  const isInfluencer =
    message.author?.attributes.type === 'vp' &&
    ['INFLUENCER'].includes(message.author.attributes.role);
  const isInternal =
    message.author?.attributes.type === 'internal' ||
    (message.author?.attributes.type === 'vp' &&
      !['VENDOR', 'VENDOR_STAFF', 'INFLUENCER'].includes(
        message.author.attributes.role,
      ));

  const badge =
    (isVendor && 'vendor') ||
    (isInfluencer && 'influencer') ||
    (isInternal && 'fn_internal') ||
    undefined;

  return (
    <Activity
      name={message.author?.friendlyName || ''}
      badgeType={badge}
      hideBar={isLast}
      tooltipContent={
        <ChatUserTooltipContent
          name=""
          chatUser={message.author}
          badgeType={badge}
        />
      }
      tooltipZIndex={100}
    >
      <Activity.Message
        name={message.author?.friendlyName || ''}
        date={message.dateCreated}
        isPersonal={identity === message.author?.identity}
        tooltipZIndex={100}
      >
        {message.attributes.type === 'tiptap' ? (
          <MessageDisplay
            content={message.body}
            mentionUser={{ userId: identity }}
          />
        ) : (
          <P>{message.body}</P>
        )}
        {message.media && message.media.length > 0 && (
          <P>
            There {message.media.length === 1 ? 'is' : 'are'}{' '}
            {message.media.length} file{message.media.length !== 1 ? 's' : ''}{' '}
            attached to this message.
          </P>
        )}
      </Activity.Message>
    </Activity>
  );
}

// -- Main ChatTab Component --

type ChatTabProps = {
  vendorId: string;
  styleColor: string;
  isReadOnly?: boolean;
  userId: string;
};

export function ChatTab({
  vendorId,
  styleColor,
  isReadOnly: isReadOnlyProp,
  userId,
}: ChatTabProps) {
  // Use userId from ChatContext for Twilio hooks to ensure cache key consistency
  // with the ChatProvider's event handlers (conversationAdded, messageAdded, etc.)
  const { client, status, userId: chatUserId } = useChatContext();

  const isConnected = client && status === 'connected';

  // Fetch backend data (uses route userId for authenticated API calls)
  const chatMessagesProcess = useGetReorderChatMessages(userId, styleColor);
  const activitiesProcess = useGetReorderActivities(userId, styleColor);

  // Fetch Twilio conversation using chatUserId for cache key consistency
  const conversationProcess = useConversationById(
    chatUserId,
    isConnected ? client : undefined,
    'reorder',
    getReorderChatId(vendorId, styleColor),
  );

  // Track whether the user has been removed from the conversation
  // (e.g. when the other app closes the chat via syncReorderChat)
  const [hasLeftConversation, setHasLeftConversation] = useState(false);

  const resolvedConversation = !conversationProcess.isError
    ? conversationProcess.data
    : undefined;
  const conversation = hasLeftConversation ? undefined : resolvedConversation;

  // Readonly when no active conversation (or when explicitly passed)
  const isReadOnly = isReadOnlyProp ?? !conversation;

  const messagesProcess = useChatMessages(chatUserId, conversation?.instance);

  // Loading state
  const isInitialLoading =
    chatMessagesProcess.isLoading || activitiesProcess.isLoading;

  // Listen for Twilio conversation lifecycle events to detect when the other
  // app opens or closes the chat. When the chat is closed, the backend removes
  // all participants (fires conversationLeft) and inactivates the conversation.
  // When opened, participants are added (fires conversationJoined).
  useEffect(() => {
    setHasLeftConversation(false);

    if (!client) return undefined;

    const reorderUniqueName = `reorder_${getReorderChatId(vendorId, styleColor)}`;

    const handleConversationLeft = (conv: Conversation) => {
      if (conv.uniqueName === reorderUniqueName) {
        setHasLeftConversation(true);
        activitiesProcess.refetch();
        chatMessagesProcess.refetch();
      }
    };

    const handleConversationRemoved = (conv: Conversation) => {
      if (conv.uniqueName === reorderUniqueName) {
        setHasLeftConversation(true);
        activitiesProcess.refetch();
        chatMessagesProcess.refetch();
      }
    };

    const handleConversationJoined = (conv: Conversation) => {
      if (conv.uniqueName === reorderUniqueName) {
        setHasLeftConversation(false);
        activitiesProcess.refetch();
        chatMessagesProcess.refetch();
      }
    };

    client.on('conversationLeft', handleConversationLeft);
    client.on('conversationRemoved', handleConversationRemoved);
    client.on('conversationJoined', handleConversationJoined);

    return () => {
      client.off('conversationLeft', handleConversationLeft);
      client.off('conversationRemoved', handleConversationRemoved);
      client.off('conversationJoined', handleConversationJoined);
    };
  }, [client, vendorId, styleColor]);

  // Build unified feed
  const feed: FeedItem[] = useMemo(() => {
    const messages =
      messagesProcess.messages?.filter(
        (m) => m.attributes.type !== 'last-message',
      ) || [];
    const backMessages = messages?.length
      ? []
      : chatMessagesProcess.data?.filter(
          (m) => m.attributes.type !== 'last-message',
        ) || [];
    const activities = activitiesProcess?.data || [];

    return [
      ...messages.map((m) => ({
        type: 'message' as const,
        data: m,
      })),
      ...backMessages.map((m) => ({
        type: 'back-message' as const,
        data: m,
      })),
      ...activities.map((a) => ({
        type: 'activity' as const,
        data: a,
      })),
    ].sort((a, b) => {
      const getDate = (item: FeedItem): Date => {
        if (item.type === 'message') {
          return item.data.instance.dateCreated || new Date();
        }
        if (item.type === 'back-message') {
          return new Date(item.data.dateCreated);
        }
        return new Date(item.data.created_at);
      };
      return getDate(a).getTime() - getDate(b).getTime();
    });
  }, [
    messagesProcess.messages,
    activitiesProcess?.data,
    chatMessagesProcess?.data,
  ]);

  // Read receipt tracking
  const lastMessageProcess = useLastMessage(chatUserId, conversation?.instance);
  useEffect(() => {
    if (
      conversation &&
      lastMessageProcess.data?.instance.index != null &&
      (conversation.instance.lastReadMessageIndex == null ||
        conversation.instance.lastReadMessageIndex <
          lastMessageProcess.data.instance.index)
    ) {
      conversation.instance.updateLastReadMessageIndex(
        lastMessageProcess.data.instance.index,
      );
    }
  }, [
    lastMessageProcess.data?.instance.index,
    conversation?.instance.lastReadMessageIndex,
  ]);

  // Auto-scroll
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const isProgrammaticScrollRef = useRef(false);
  const prevFeedLengthRef = useRef(0);

  useEffect(() => {
    const isInitialLoad = prevFeedLengthRef.current === 0 && feed.length > 0;
    prevFeedLengthRef.current = feed.length;

    if (isInitialLoad || isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      isProgrammaticScrollRef.current = true;
    }
  }, [feed.length]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false;
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    isAtBottomRef.current = scrollHeight - (scrollTop + clientHeight) < 40;
  };

  // Render activity items
  const renderActivity = (
    activity: ReorderWorksheetActivity,
    isLast: boolean,
  ) => {
    switch (activity.event_type) {
      case ReorderWorksheetActivityEnum.OPEN_CHAT:
        return (
          <OpenChatActivity
            key={activity.event_id}
            activity={activity}
            isLast={isLast}
          />
        );
      case ReorderWorksheetActivityEnum.CLOSE_CHAT:
        return (
          <CloseChatActivity
            key={activity.event_id}
            activity={activity}
            isLast={isLast}
          />
        );
      case ReorderWorksheetActivityEnum.UPDATE_FORM:
        return (
          <UpdateFormActivity
            key={activity.event_id}
            activity={activity}
            isLast={isLast}
          />
        );
      default:
        return null;
    }
  };

  // Empty state when no chat exists
  const isEmpty =
    !isInitialLoading && feed.length === 0 && !conversationProcess.isLoading;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div
        className="flex-grow overflow-y-auto overflow-x-hidden p-4 px-8 flex flex-col gap-1"
        onScroll={handleScroll}
      >
        {/* Load more messages */}
        {!isInitialLoading && messagesProcess.hasPreviousPage && (
          <div className="w-full flex justify-center p-4">
            <Button
              loading={messagesProcess.isFetchingPreviousPage}
              onClick={() => messagesProcess.fetchPreviousPage()}
            >
              Load more messages
            </Button>
          </div>
        )}

        {/* Feed items */}
        {!isInitialLoading && (
          <ForEach items={feed}>
            {(event, index) => {
              if (event.type === 'activity') {
                return renderActivity(event.data, index === feed.length - 1);
              }
              if (event.type === 'back-message') {
                return (
                  <ChatBackMessageItem
                    key={event.data.sid}
                    message={event.data}
                    isLast={index === feed.length - 1}
                  />
                );
              }
              return (
                <ConversationViewMessage
                  key={event.data.sid}
                  message={event.data}
                  isLast={index === feed.length - 1}
                />
              );
            }}
          </ForEach>
        )}

        {/* Loading state */}
        {isInitialLoading && (
          <div className="flex-1 flex items-center justify-center">
            <Icon.LoadingCircle />
          </div>
        )}

        {/* Empty state */}
        {isEmpty && (
          <div className="flex-1 flex flex-col items-center justify-center gap-2">
            <Icon
              name="chat/chat-default"
              className="text-3xl text-neutral-400"
            />
            <P size="sm" color="disabled">
              No messages yet
            </P>
          </div>
        )}

        <span ref={messagesEndRef} />
      </div>

      {/* Message input (hidden in read-only mode) */}
      {!isReadOnly && conversation && (
        <MessageInput conversation={conversation} />
      )}

      {/* Connection status messages */}
      {!isReadOnly && !isConnected && !isInitialLoading && (
        <div className="p-4 border-t border-neutral-300 w-full flex justify-center">
          <P color="disabled" size="sm">
            Connecting to chat service...
          </P>
        </div>
      )}

      {!isReadOnly &&
        isConnected &&
        !conversation &&
        !conversationProcess.isLoading &&
        !isEmpty && (
          <div className="p-4 border-t border-neutral-300 w-full flex justify-center">
            <P color="disabled" size="sm">
              Chat is not active
            </P>
          </div>
        )}
    </div>
  );
}
